import { CreatorTagChips } from '../CreatorIntelligence/CreatorTagChips';
import React, { useState } from 'react';
import {
  IconTrash, IconTrendingUp, IconTrendingDown, IconExternalLink, IconEye
} from '@tabler/icons-react';
import GradientProgressBar from '../../common/GradientProgressBar';
import { getCreatorStatusColor, getCreatorStatusLabel, getCreatorTypeColor, getCreatorTypeLabel } from '../../../constants/statuses';
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell
} from '../../ui/data-table';
import { Checkbox } from '../../ui/checkbox';
import {
  Pagination, PaginationContent, PaginationPrevious, PaginationNext,
  PaginationLink, PaginationEllipsis, PaginationInfo, PaginationPageSize
} from '../../ui/pagination';
import { formatNumber } from '../../../utils/formatters';
import { CreatorNotesPreview } from './panels/CreatorNotesTab';

// Higher = better review status. Drives sorting for the "Review Status" column.
const STATUS_RANK = {
  'Strong Enough': 100,
  'High-Priority': 90,
  'High Priority': 90,
  '(Persona) High Priority': 85,
  'Strong Enough If We Need a Lot': 80,
  'Pre-Approved': 70,
  'Approved': 60,
  'Persona Team 1': 50,
  'Persona Team 2': 50,
  'Low Priority': 40,
  'Persona Low Priority': 40,
  'Persona: Team 3': 30,
  'BrikctoRecruit': 20,
  'In Reviewing': 15,
  'Last-Resort': 10,
  'Not Strong Enough': 5,
  'NeedsToSet': 0,
};

const CreatorTable = ({ creators, onView, onDelete, onUpdate, onNotesEditingChange, onNotesFocus, selectedCreators, onSelectCreator, onSelectAll, canManageCreators = true }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [showScore, setShowScore] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const changeEditing = (id, editing) => { setEditingId(editing ? id : null); onNotesEditingChange?.(editing); };

  const calculateGrowth = (creator) => {
    if (!creator?.twitter?.followersHistory || creator.twitter.followersHistory.length < 2) {
      return { change: 0, percent: 0, isPositive: true };
    }
    const history = creator.twitter.followersHistory;
    const currentFollowers = creator.twitter.followers;
    const oldestFollowers = history[0].count;
    const change = currentFollowers - oldestFollowers;
    const percent = oldestFollowers > 0 ? ((change / oldestFollowers) * 100) : 0;
    return { change, percent, isPositive: change >= 0 };
  };

  const handleSort = (key) => {
    if (editingId) return;
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const getSortValue = (creator, key) => {
    switch (key) {
      case 'creator':
        return (creator.twitter?.username || '').toLowerCase();
      case 'followers':
        return creator.twitter?.followers ?? 0;
      case 'score':
        return creator.advancedMetrics?.score ?? -1;
      case 'status':
        return STATUS_RANK[creator.status] ?? -1;
      default:
        return 0;
    }
  };

  const calculatedOrder = React.useMemo(() => {
    if (!sortKey) return creators;
    const arr = [...creators];
    arr.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [creators, sortKey, sortDir]);
  const frozenOrder = React.useRef([]);
  if (!editingId) frozenOrder.current = calculatedOrder.map(creator => creator._id);
  const creatorById = new Map(creators.map(creator => [creator._id, creator]));
  const sortedCreators = editingId ? frozenOrder.current.map(id => creatorById.get(id)).filter(Boolean) : calculatedOrder;

  const totalPages = Math.ceil(sortedCreators.length / pageSize);
  const startIdx = (page - 1) * pageSize;
  const paginatedCreators = sortedCreators.slice(startIdx, startIdx + pageSize);
  const allSelected = selectedCreators && paginatedCreators.length > 0 && paginatedCreators.every(c => selectedCreators.has(c._id));

  // Reset to page 1 if current page is out of bounds
  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(1);
  }, [page, totalPages]);

  const handlePageSizeChange = (newSize) => {
    if (editingId) return;
    setPageSize(newSize);
    setPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('ellipsis-start');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < totalPages - 2) pages.push('ellipsis-end');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div>
      <label className="mb-3 flex min-h-11 items-center justify-end gap-2 text-sm text-text-secondary">
        <input type="checkbox" checked={showScore} onChange={event => setShowScore(event.target.checked)} /> Show score
      </label>
      {/* One responsive table keeps active note editors mounted across breakpoints. */}
      <div>
        <DataTable style={{ tableLayout: 'fixed' }} className={`[&_table]:block sm:[&_table]:table ${showScore ? 'sm:[&_table]:min-w-[1060px]' : 'sm:[&_table]:min-w-[960px]'}`}>
          <DataTableHeader className="hidden sm:table-header-group">
            <tr>
              {canManageCreators && (
                <DataTableHead className="px-3" style={{ width: 44 }}>
                  <Checkbox
                    aria-label="Select loaded creators"
                    checked={allSelected}
                    onCheckedChange={(checked) => onSelectAll && onSelectAll(!!checked)}
                  />
                </DataTableHead>
              )}
              <DataTableHead
                sortable
                tabIndex={0}
                aria-sort={sortKey === 'creator' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onKeyDown={event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); handleSort('creator'); } }}
                sorted={sortKey === 'creator' ? sortDir : null}
                onSort={() => handleSort('creator')}
                style={{ width: 180 }}
              >
                Creator
              </DataTableHead>
              <DataTableHead className="normal-case tracking-normal">Creator notes</DataTableHead>
              <DataTableHead
                sortable
                tabIndex={0}
                aria-sort={sortKey === 'followers' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onKeyDown={event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); handleSort('followers'); } }}
                sorted={sortKey === 'followers' ? sortDir : null}
                onSort={() => handleSort('followers')}
                style={{ width: 100 }}
              >
                Followers
              </DataTableHead>
              <DataTableHead className="normal-case tracking-normal" style={{ width: 110 }}>Recorded rate</DataTableHead>
              {showScore && <DataTableHead
                sortable
                tabIndex={0}
                aria-sort={sortKey === 'score' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onKeyDown={event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); handleSort('score'); } }}
                sorted={sortKey === 'score' ? sortDir : null}
                onSort={() => handleSort('score')}
                style={{ width: 100 }}
              >
                <span title="Advanced creator score (0-10,000) based on engagement, content quality, and audience">Score</span>
              </DataTableHead>}
              <DataTableHead
                sortable
                tabIndex={0}
                aria-sort={sortKey === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onKeyDown={event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); handleSort('status'); } }}
                sorted={sortKey === 'status' ? sortDir : null}
                onSort={() => handleSort('status')}
                style={{ width: 115 }}
              >
                Review Status
              </DataTableHead>
              <DataTableHead style={{ width: 204 }}><span className="sr-only">Actions</span></DataTableHead>
            </tr>
          </DataTableHeader>
          <DataTableBody className="block sm:table-row-group">
            {paginatedCreators.map((creator) => {
              const growth = calculateGrowth(creator);

              return (
                <DataTableRow
                  key={creator._id}
                  selected={selectedCreators && selectedCreators.has(creator._id)}
                  className="grid grid-cols-2 items-start py-3 sm:table-row sm:py-0 [&>td]:min-w-0 [&>td]:align-top"
                >
                  {/* Checkbox */}
                  {canManageCreators && <DataTableCell className="col-span-2 px-3 whitespace-nowrap">
                    <Checkbox
                      aria-label={`Select @${creator.twitter?.username || 'creator'}`}
                      checked={selectedCreators && selectedCreators.has(creator._id)}
                      onCheckedChange={() => onSelectCreator && onSelectCreator(creator._id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DataTableCell>}

                  {/* Creator Info */}
                  <DataTableCell className="col-span-2 py-3 overflow-hidden">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="flex-shrink-0">
                        <img
                          src={creator.twitter?.profileImageUrl || `https://unavatar.io/twitter/${creator.twitter?.username}`}
                          alt={`@${creator.twitter?.username}`}
                          className="h-9 w-9 object-cover ring-1 ring-surface-3"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '';
                            e.target.className = 'h-9 w-9 bg-accent-muted';
                          }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <a
                          href={`https://twitter.com/${creator.twitter?.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-text-primary hover:text-accent flex items-center text-sm min-w-0"
                        >
                          <span className="truncate">@{creator.twitter?.username}</span>
                          <IconExternalLink className="h-3 w-3 ml-1.5 opacity-40 flex-shrink-0" />
                        </a>
                        {creator.creatorType?.length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {creator.creatorType.map(type => (
                              <span key={type} className={`inline-flex items-center px-1.5 py-0 text-[10px] font-medium rounded ${getCreatorTypeColor(type)}`}>
                                {getCreatorTypeLabel(type)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </DataTableCell>

                  <DataTableCell className="col-span-2 py-4">
                    <CreatorNotesPreview creator={creator} onUpdate={onUpdate} readOnly={!canManageCreators} editing={editingId === creator._id} canStartEdit={!editingId} onEditingChange={editing => changeEditing(creator._id, editing)} />
                    {creator.tagIds?.length > 0 && <div className="mt-2"><CreatorTagChips tagIds={creator.tagIds} max={4} compact /></div>}
                    {canManageCreators && onNotesFocus && <button type="button" disabled={!!editingId}
                      className="min-h-11 text-xs text-text-secondary hover:text-accent disabled:opacity-50"
                      aria-label={`Focus notes for @${creator.twitter?.username || 'creator'}`}
                      onClick={() => onNotesFocus(creator)}>Open in notes focus</button>}
                  </DataTableCell>

                  {/* Followers */}
                  <DataTableCell className="whitespace-nowrap">
                    <span className="block text-xs text-text-tertiary sm:hidden">Followers</span>
                    <div>
                      <div className="font-medium text-text-primary text-sm">
                        {Number.isFinite(creator.twitter?.followers) ? formatNumber(creator.twitter.followers) : 'Unknown'}
                      </div>
                      {growth.change !== 0 && (
                        <div
                          className={`flex items-center text-xs mt-0.5 ${growth.isPositive ? 'text-accent' : 'text-gray-400'}`}
                          title={`${growth.isPositive ? '+' : ''}${formatNumber(growth.change)} followers since first tracked`}
                        >
                          {growth.isPositive ? (
                            <IconTrendingUp className="h-3 w-3 mr-0.5" />
                          ) : (
                            <IconTrendingDown className="h-3 w-3 mr-0.5" />
                          )}
                          {growth.percent !== 0 && `${growth.isPositive ? '+' : ''}${Math.abs(growth.percent).toFixed(1)}%`}
                        </div>
                      )}
                    </div>
                  </DataTableCell>

                  <DataTableCell>
                    <span className="block text-xs text-text-tertiary sm:hidden">Recorded rate</span>
                    {Number.isFinite(creator.wallet?.defaultPayment) && creator.wallet.defaultPayment > 0
                      ? <><span>{creator.wallet.defaultPayment.toLocaleString()} {creator.wallet.currency || '(currency unknown)'}</span><span className="block text-xs text-text-tertiary">Default rate</span></>
                      : <span className="text-text-secondary">Not quoted</span>}
                  </DataTableCell>

                  {/* Score */}
                  {showScore && <DataTableCell className="whitespace-nowrap">
                    {creator.advancedMetrics?.score !== undefined ? (
                      <div className="w-24" title={`Score: ${Math.round(creator.advancedMetrics.score).toLocaleString()} / 10,000`}>
                        <div className="text-sm font-medium text-text-primary mb-1">
                          {Math.round(creator.advancedMetrics.score).toLocaleString()}
                        </div>
                        <GradientProgressBar
                          progress={(creator.advancedMetrics.score / 10000) * 100}
                          value={creator.advancedMetrics.score}
                          compact
                        />
                      </div>
                    ) : (
                      <span className="text-text-tertiary text-sm">No data</span>
                    )}
                  </DataTableCell>}

                  {/* Status */}
                  <DataTableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium ${getCreatorStatusColor(creator.status)}`}
                      title={creator.status}
                    >
                      {getCreatorStatusLabel(creator.status)}
                    </span>
                  </DataTableCell>

                  {/* Actions */}
                  <DataTableCell className="col-span-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(creator, e);
                        }}
                        className="inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                      >
                        <IconEye className="h-3.5 w-3.5" />
                        Open profile
                      </button>
                      {canManageCreators && <button
                        disabled={!!editingId}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(creator._id);
                        }}
                        className="inline-flex h-11 w-11 items-center justify-center hover:bg-surface-3 text-gray-400"
                        aria-label={`Delete @${creator.twitter?.username || 'creator'}`}
                        title="Delete Creator"
                      >
                        <IconTrash className="h-4 w-4" />
                      </button>}
                    </div>
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </DataTableBody>
        </DataTable>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <PaginationInfo
            currentPage={page}
            pageSize={pageSize}
            totalItems={creators.length}
          />
          <PaginationContent>
            <PaginationPrevious
              disabled={page === 1 || !!editingId}
              onClick={() => { if (!editingId) setPage(p => Math.max(1, p - 1)); }}
            />
            {getPageNumbers().map((p, idx) =>
              typeof p === 'string' ? (
                <PaginationEllipsis key={p} />
              ) : (
                <PaginationLink
                  key={p}
                  isActive={p === page}
                  onClick={() => { if (!editingId) setPage(p); }}
                >
                  {p}
                </PaginationLink>
              )
            )}
            <PaginationNext
              disabled={page === totalPages || !!editingId}
              onClick={() => { if (!editingId) setPage(p => Math.min(totalPages, p + 1)); }}
            />
          </PaginationContent>
          <PaginationPageSize
            value={pageSize}
            onChange={handlePageSizeChange}
            options={[25, 50, 100]}
          />
        </Pagination>
      )}
    </div>
  );
};

export default CreatorTable;
